import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, WifiOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PlatformLogoBadge } from './PlatformLogo';
import type { SocialPlatform } from '@/client/types/media';
import {
  useImportProviderAccountsMutation,
  useProviderAccountsQuery,
} from '@/client/api/marketing/sources';
import type { ApiProviderAccount } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';

function friendlyProviderError(error: unknown, provider: string) {
  const raw = getApiErrorMessage(error, '');
  const status = (error as { response?: { status?: number }; status?: number })?.response
    ?.status ?? (error as { status?: number })?.status;
  const looksMissing =
    status === 404 ||
    /^not found\b/i.test(raw) ||
    /:\s*https?:\/\//i.test(raw);

  if (looksMissing) {
    return `This import route is not available on the API yet. Ask for GET …/sources/providers/${provider}/accounts on staging.`;
  }

  // Never paste raw URLs into the dialog — they force overflow/scroll for no user value.
  const cleaned = raw.replace(/https?:\/\/\S+/g, '').replace(/\s{2,}/g, ' ').trim();
  return cleaned || 'The provider did not respond';
}

/** Vendors (esp. Zernio) send camelCase / `_id`; prefer canonical fields when present. */
function accountLabel(a: ApiProviderAccount): {
  id: string;
  title: string;
  handle: string | null;
} {
  const id = String(a.account_id || a.id || a._id || '').trim();
  const handleRaw = a.handle || a.username || null;
  const handle = handleRaw
    ? String(handleRaw).replace(/^@/, '').trim() || null
    : null;
  const title =
    a.display_name ||
    a.displayName ||
    a.name ||
    handle ||
    id ||
    'Unknown account';
  return { id, title: String(title), handle };
}

function resolveSaid(a: ApiProviderAccount): number | undefined {
  const said = a.said ?? a.matched_said;
  return typeof said === 'number' && Number.isFinite(said) ? said : undefined;
}

/**
 * Import accounts that were connected in the provider's own dashboard.
 *
 * This closes the gap that makes people ask "I connected four networks, why
 * can't I see them?" — our OAuth callback is what creates a connection, so
 * anything linked at the vendor lives only there until it is adopted.
 *
 * Already-adopted rows stay selectable: re-import is idempotent on the provider
 * side and repairs a missing `mkt_source_connection` when adopt succeeded but
 * the local link row never wrote.
 */
export function ImportProviderAccountsDialog({
  open,
  provider,
  onClose,
}: {
  open: boolean;
  provider: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = useProviderAccountsQuery(provider, open);
  const importAccounts = useImportProviderAccountsMutation();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const accounts: ApiProviderAccount[] = useMemo(() => data?.accounts ?? [], [data]);

  const selectableIds = useMemo(
    () => accounts.map((a) => accountLabel(a).id).filter(Boolean),
    [accounts],
  );
  const newIds = useMemo(
    () =>
      accounts
        .filter((a) => !a.adopted)
        .map((a) => accountLabel(a).id)
        .filter(Boolean),
    [accounts],
  );
  const adoptedCount = accounts.filter((a) => a.adopted).length;

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const noneSelected = selected.size === 0;

  const selectAll = () => setSelected(new Set(selectableIds));
  const selectNew = () => setSelected(new Set(newIds));
  const selectNone = () => setSelected(new Set());

  // Preselect net-new rows. Already-adopted stay available but unchecked so a
  // first import does not surprise-rehit every linked account.
  useEffect(() => {
    if (open) setSelected(new Set(newIds));
  }, [open, newIds]);

  const toggle = (id: string) => {
    if (!id) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = () => {
    const chosen = accounts.filter((a) => selected.has(accountLabel(a).id));
    if (chosen.length === 0) {
      toast.error('Select at least one account');
      return;
    }
    importAccounts.mutate(
      {
        provider,
        accounts: chosen.map((a) => {
          const { id, title, handle } = accountLabel(a);
          return {
            account_id: id,
            platform: a.platform,
            handle: handle ?? '',
            display_name: title,
            // Prefer an existing registry id (adopted or matched) so we link
            // rather than mint a duplicate handle+platform row.
            said: resolveSaid(a),
            profile_id: a.profile_id ?? undefined,
            target_id: a.target_id ?? undefined,
          };
        }),
      },
      {
        onSuccess: (res) => {
          const ok = res?.data?.imported_count ?? 0;
          const bad = res?.data?.failed ?? [];
          if (bad.length === 0) {
            toast.success(
              ok === 0
                ? 'Nothing new to import'
                : `Imported ${ok} account${ok === 1 ? '' : 's'}`,
            );
            onClose();
            return;
          }
          // Partial success is the expected case — one expired token should not
          // read as a total failure, nor be swallowed by a success toast.
          toast.warning(`Imported ${ok}, ${bad.length} failed`, {
            description: bad.map((f) => `${f.account_id}: ${f.error}`).join('; ').slice(0, 200),
          });
          refetch();
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not import the accounts')),
      },
    );
  };

  const selectedCount = selected.size;
  const selectedAdopted = accounts.filter(
    (a) => a.adopted && selected.has(accountLabel(a).id),
  ).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import from {provider}</DialogTitle>
          <DialogDescription>
            Accounts connected in {provider}’s own dashboard. They stay invisible here until
            imported — publishing resolves our own connection record, not the vendor’s. Already
            imported rows can be re-selected to repair a missing local link.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <p className="py-8 text-center text-sm text-gray-500">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
            Asking {provider} what it holds…
          </p>
        )}

        {!isLoading && isError && (
          <div className="py-6 text-center text-sm">
            <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
            <div className="font-medium text-amber-800">Could not reach {provider}</div>
            <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500">
              {friendlyProviderError(error, provider)}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => refetch()}
            >
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !isError && accounts.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">
            {provider} reports no connected accounts. Connect one in its dashboard first, then
            reopen this.
          </p>
        )}

        {!isLoading && !isError && accounts.length > 0 && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2 px-0.5">
              <p className="text-xs text-gray-500">
                {selectedCount === 0
                  ? `None selected · ${newIds.length} new · ${adoptedCount} already imported`
                  : allSelected
                    ? `All ${selectedCount} selected`
                    : `${selectedCount} selected${
                        selectedAdopted ? ` (${selectedAdopted} re-link)` : ''
                      }`}
              </p>
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  onClick={selectNew}
                  disabled={newIds.length === 0 || newIds.every((id) => selected.has(id))}
                  className="rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  New only
                </button>
                <span className="text-gray-300" aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  onClick={selectAll}
                  disabled={allSelected || selectableIds.length === 0}
                  className="rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  Select all
                </button>
                <span className="text-gray-300" aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  onClick={selectNone}
                  disabled={noneSelected}
                  className="rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  None
                </button>
              </div>
            </div>

            <div className="max-h-[min(50vh,20rem)] space-y-1 overflow-y-auto overscroll-contain">
              {accounts.map((a, index) => {
                const { id, title, handle } = accountLabel(a);
                const rowKey = id || `row-${index}`;
                return (
                  <label
                    key={rowKey}
                    className={cn(
                      'flex items-center gap-2.5 rounded-md border px-2.5 py-2 text-sm',
                      selected.has(id)
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 rounded border-gray-300"
                      checked={!!id && selected.has(id)}
                      onChange={() => toggle(id)}
                      disabled={!id}
                    />
                    <PlatformLogoBadge platform={a.platform as SocialPlatform} size="sm" />
                    <span className="min-w-0 flex-1 truncate text-gray-900">
                      <span className="font-medium">{title}</span>
                      {handle && title.toLowerCase() !== handle.toLowerCase() && (
                        <span className="ml-1.5 font-normal text-gray-500">
                          @{handle}
                        </span>
                      )}
                      {!handle && id && title !== id && (
                        <span className="ml-1.5 font-normal text-gray-400">{id}</span>
                      )}
                    </span>
                    {a.adopted ? (
                      <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> already imported
                      </span>
                    ) : a.matched_said ? (
                      <span
                        className="shrink-0 text-[11px] text-sky-700"
                        title={`Will link to the existing "${a.matched_display_name ?? ''}" record`}
                      >
                        links to existing
                      </span>
                    ) : (
                      <span className="shrink-0 text-[11px] text-gray-400">new</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={importAccounts.isPending || accounts.length === 0 || selectedCount === 0}
          >
            {importAccounts.isPending
              ? 'Importing…'
              : selectedAdopted > 0 && selectedAdopted === selectedCount
                ? `Re-link ${selectedCount}`
                : allSelected && selectedCount > 1
                  ? `Import all ${selectedCount}`
                  : selectedCount === 0
                    ? 'Import'
                    : selectedAdopted > 0
                      ? `Import / re-link ${selectedCount}`
                      : `Import ${selectedCount}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
